import React from 'react'
import PropTypes from 'prop-types'
import { isNil, times } from 'lodash'
import styles from './codeInput.css'
import setStateAsync from '../../helpers/setStateAsync'

const preventDefaultEventHandling = (event) => {
  event.preventDefault()
  event.stopPropagation()
}

export default class CodeInput extends React.Component {
  constructor (props) {
    super(props)
    const { code, length } = props

    this.state = {
      code,
      currentIndex: 0,
      maxLength: length
    }

    this.handleChange = this.handleChange.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.focus = this.focus.bind(this)
    this.focusInputField = this.focusInputField.bind(this)
    this.focusInputFieldForCurrentIndex = this.focusInputFieldForCurrentIndex.bind(this)
    this.next = this.next.bind(this)
    this.previous = this.previous.bind(this)
    this.renderInputs = this.renderInputs.bind(this)
    this.setCurrentIndex = this.setCurrentIndex.bind(this)
    this.setStateAsync = setStateAsync.bind(this)
  }

  async componentWillMount () {
    const { code, maxLength } = this.state
    const currentIndex = code.length - 1

    const updatedCode = [...code]
    updatedCode.length = maxLength
    await this.setStateAsync({ code: updatedCode, currentIndex })
  }

  async handleChange (event) {
    const { target } = event
    const { value } = target
    const { currentIndex, maxLength } = this.state
    const { onChange } = this.props

    // only one character per input
    if (value.length > 1) {
      preventDefaultEventHandling(event)

      if (currentIndex < maxLength - 1) {
        const newValue = target.value[1]
        this.refs[currentIndex + 1].value = newValue
        this.next(newValue)
      }
      target.value = this.state.code[currentIndex]
      return
    }

    const { code } = this.state
    const newCode = [...code]
    newCode[currentIndex] = value

    await this.setStateAsync ({ code: newCode })
    if (value) this.next()
      onChange(newCode.join(''))
  }

  async handleFocus ({ target }) {
    const index = parseInt(target.attributes['data-id'].value, 10)
    await this.setCurrentIndex(index)
  }

  async handleKeyDown (event) {
    const { nativeEvent } = event
    const { key, altKey, ctrlKey, metaKey, shiftKey } = nativeEvent
    event.persist() // https://reactjs.org/docs/events.html#event-pooling

    if (altKey || ctrlKey || metaKey || shiftKey) return true

    switch (key) {
      case 'ArrowRight': {
        preventDefaultEventHandling(event)
        await this.next()
        break
      }
      case 'ArrowLeft': {
        preventDefaultEventHandling(event)
        await this.previous()
        break
      }
      case 'Backspace': {
        preventDefaultEventHandling(event)
        const { currentIndex } = this.state
        const previousIndex = (currentIndex - 1) < 0 ? 0 : (currentIndex - 1)
        if (!this.refs[currentIndex].value) {
          await this.previous()
          const previousField = this.refs[previousIndex]
          if (previousField.value) {
            previousField.value = ''
          }
        }
        this.refs[currentIndex].value = ''
        await this.handleChange(event)
        break
      }
      default: {
        return true
      }
    }
  }

  async focus () {
    await this.focusInputFieldForCurrentIndex()
  }

  async focusInputFieldForCurrentIndex () {
    const { currentIndex } = this.state
    await this.focusInputField(currentIndex)
  }

  async focusInputField (index) {
    await this.setCurrentIndex(index)

    const { currentIndex } = this.state
    this.refs[currentIndex].focus()
    this.refs[currentIndex].setSelectionRange(0, 1)
  }

  async next (value) {
    const { onChange } = this.props
    const { currentIndex } = this.state
    const nextIndex = currentIndex + 1
    await this.setCurrentIndex(nextIndex)

    const { code } = this.state
    const newCode = [].concat(code)
    if (value) {
      newCode[nextIndex] = value
    }

    await this.setStateAsync({ code: newCode })
    await this.focusInputFieldForCurrentIndex()
    onChange(newCode.join(''))
  }

  async previous () {
    const { currentIndex } = this.state
    const nextIndex = currentIndex - 1
    await this.setCurrentIndex(nextIndex)
    await this.focusInputFieldForCurrentIndex()
  }

  async setCurrentIndex (index) {
    const { maxLength } = this.state
    const minIndex = 0
    const maxIndex = maxLength - 1

    let newIndex = isNil(index) ? 0 : index
    if (index < minIndex) {
      newIndex = minIndex
    } else if (index > maxIndex) {
      newIndex = maxIndex
    }

    await this.setStateAsync({ currentIndex: newIndex })
  }

  renderInputs () {
    const { length } = this.props

    const inputs = times(length).map((i) => {
      return (<input
        type='text'
        key={i}
        defaultValue={this.state.code[i] || ''}
        data-id={i}
        className={styles.charInput}
        style={{ maxWidth: `calc(100% / ${length})` }}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        ref={i}
      />)
    })

    return inputs
  }

  render () {
    const { hint } = this.props
    const hintStyles = [styles.hint, styles[hint.type]]

    return (
      <div className={styles.wrapper}>
        <div className={styles.base}>
          {this.renderInputs()}
        </div>
        <span className={hintStyles.join(' ')}>{hint.text}</span>
      </div>
    )
  }
}

CodeInput.propTypes = {
  hint: PropTypes.object,
  inputRef: PropTypes.func,
  length: PropTypes.number,
  code: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired
}

CodeInput.defaultProps = {
  hint: { type: 'info', text: '' },
  code: []
}



// WEBPACK FOOTER //
// ./src/components/inputs/codeInput.jsx